/** Bounded page-wide requests plus measured, server-aware upload concurrency. */
let chatUsesPolling = false;
export function setUploadChatTransport(transport?: string) { chatUsesPolling = transport === 'polling'; }

export function uploadConnectionLimit(protocol: string, polling = false): number {
  // HTTP/1 has a small per-origin connection pool shared with tables/chat.
  // Multiplexed connections can use the full server/throughput-based budget.
  return protocol === 'h2' || protocol === 'h3' || protocol.startsWith('h3-') ? 16 : polling ? 3 : 4;
}

export function browserUploadConnectionLimit(startPath: string): number {
  if (typeof performance.getEntriesByName !== 'function') return uploadConnectionLimit('', chatUsesPolling);
  const url = new URL(startPath, window.location.href).href;
  const entry = performance.getEntriesByName(url).at(-1) as PerformanceResourceTiming | undefined;
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  return uploadConnectionLimit(entry?.nextHopProtocol || navigation?.nextHopProtocol || '', chatUsesPolling);
}

export class UploadSlots {
  private active = 0;
  private waiting: Array<{ limit: number; grant: () => void }> = [];
  constructor(private limit: number) {}
  setLimit(limit: number) { this.limit = limit; this.pump(); }
  private pump() {
    for (let i = 0; i < this.waiting.length;) {
      const item = this.waiting[i];
      if (this.active >= Math.min(this.limit, item.limit)) { i++; continue; }
      this.waiting.splice(i, 1); item.grant();
    }
  }
  acquire(signal: AbortSignal, ceiling = 16): Promise<() => void> {
    return new Promise((resolve, reject) => {
      const item = { limit: ceiling, grant: () => {
        signal.removeEventListener('abort', abort);
        this.active++;
        let released = false;
        resolve(() => { if (!released) { released = true; this.active--; this.pump(); } });
      } };
      const abort = () => {
        const index = this.waiting.indexOf(item);
        if (index >= 0) this.waiting.splice(index, 1);
        reject(new DOMException('Загрузка остановлена', 'AbortError'));
      };
      if (signal.aborted) { abort(); return; }
      signal.addEventListener('abort', abort, { once: true });
      this.waiting.push(item); this.pump();
    });
  }
}

export class AdaptiveUploadConcurrency {
  limit: number;
  private ceiling: number;
  private started: number;
  private bytes = 0;
  private samples = 0;
  private previous?: { limit: number; rate: number };
  private stableRate = 0;
  private probeAfter = 0;
  constructor(private max: number, ceiling: number, private now = () => performance.now()) {
    this.ceiling = Math.max(1, Math.min(max, ceiling));
    this.limit = Math.min(4, this.ceiling); this.started = now();
  }
  observe(bytes: number, ceiling: number): number {
    const now = this.now();
    this.ceiling = Math.max(1, Math.min(this.max, ceiling));
    if (this.limit > this.ceiling) {
      this.limit = this.ceiling; this.previous = undefined; this.stableRate = 0;
      this.reset(now); return this.limit;
    }
    this.bytes += bytes; this.samples++;
    // Whole-window throughput, not one fast reply; never add requests for
    // a rise that did not help, and leave time before probing the same link again.
    if (now - this.started < 1000 || this.samples < this.limit) return this.limit;
    const rate = this.bytes / Math.max(1, now - this.started);
    if (this.previous) {
      if (rate < this.previous.rate * 1.08) {
        this.limit = Math.min(this.ceiling, this.previous.limit);
        this.probeAfter = now + 15000;
      }
      this.previous = undefined; this.stableRate = rate;
    } else if (this.stableRate && rate < this.stableRate * 0.7 && this.limit > 1) {
      // Completion bursts are not evidence that fewer lanes are faster. Keep
      // the initial four unless the server or connection budget requires less.
      this.limit = Math.max(Math.min(4, this.ceiling), Math.floor(this.limit / 2));
      this.probeAfter = now + 5000; this.stableRate = 0;
    } else {
      this.stableRate = rate;
      if (now >= this.probeAfter && this.limit < this.ceiling) {
        this.previous = { limit: this.limit, rate };
        this.limit = Math.min(this.ceiling, this.limit * 2);
      }
    }
    this.reset(now); return this.limit;
  }
  private reset(now: number) { this.started = now; this.bytes = 0; this.samples = 0; }
}
